import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BellRing,
  LayoutDashboard,
  Palette,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Stethoscope,
} from 'lucide-react';
import { Button, Eyebrow } from '../components/ui';
import { Reveal } from '../components/Reveal';

/**
 * Portada de Trellis como producto, con la marca de Dx Tech.
 *
 * Sigue el lenguaje del sitio de Dx Tech: hero oscuro con resplandores que
 * derivan lento, píldoras blancas sobre fondo oscuro, bandas de sección
 * alternadas y aparición escalonada al scrollear.
 */
export function Landing() {
  return (
    <div className="min-h-screen bg-bg overflow-x-hidden">
      <Hero />
      <Capabilities />
      <HowItWorks />
      <Closing />

      <footer className="mx-auto max-w-7xl px-6 py-10 flex flex-wrap items-center justify-between gap-4 text-sm text-fg-muted">
        <span>Trellis · una plataforma de Dx Tech</span>
        <div className="h-1 w-24 rounded-full" style={{ background: 'var(--tr-gradient)' }} />
      </footer>
    </div>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden text-white" style={{ background: '#1a1f2e' }}>
      {/* Los resplandores van como capas propias, no como fondo del section, para
          poder animarlos sin repintar el resto. */}
      <div
        className="pointer-events-none absolute -top-40 -left-32 size-[820px] rounded-full blur-3xl opacity-60 animate-drift"
        style={{ background: 'radial-gradient(circle, rgba(229,119,251,0.45), transparent 65%)' }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -top-24 right-[-15%] size-[720px] rounded-full blur-3xl opacity-70 animate-drift"
        style={{
          background: 'radial-gradient(circle, rgba(51,92,255,0.55), transparent 65%)',
          animationDelay: '-11s',
        }}
        aria-hidden
      />

      <header className="relative mx-auto max-w-7xl px-6 pt-8 safe-top flex items-center justify-between">
        <img src="/brand/dxtech-isotype.svg" alt="Dx Tech" className="size-9" />
        {/* A la puerta común, no a /admin/login. Esa ruta sigue existiendo
            porque es la URI de retorno registrada en Azure, pero es un detalle
            de Entra: quien entra desde acá puede ser de cualquier institución. */}
        <Link
          to="/ingresar"
          className="text-sm text-white/70 hover:text-white transition-colors duration-200"
        >
          Ingresar
        </Link>
      </header>

      <div className="relative mx-auto max-w-7xl px-6 pt-12 pb-16 md:pt-16 md:pb-20">
        <div className="animate-fade-up">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 text-[11px] font-semibold tracking-[0.14em] uppercase text-white/80 backdrop-blur">
            <Sparkles className="size-3.5" />
            Programas de pacientes
          </span>
        </div>

        <h1
          className="mt-6 max-w-3xl text-[2.6rem] md:text-6xl font-semibold leading-[1.03] tracking-[-0.03em] animate-fade-up"
          style={{ animationDelay: '80ms' }}
        >
          Cada institución arma su programa.
          <br />
          <span className="text-white/50">Sin escribir una línea de código.</span>
        </h1>

        <p
          className="mt-6 max-w-xl text-lg leading-relaxed text-white/70 animate-fade-up"
          style={{ animationDelay: '160ms' }}
        >
          Trellis es la plataforma de Dx Tech para acompañar a pacientes en tratamiento. La
          institución define su marca, sus contenidos, qué le pide registrar a cada paciente y qué
          dispara un aviso a su equipo de seguimiento.
        </p>

        <div
          className="mt-8 flex flex-wrap items-center gap-3 animate-fade-up"
          style={{ animationDelay: '240ms' }}
        >
          {/* La simulación va primero, y el ingreso queda de secundario.
              Quien llega acá sin cuenta viene a ver qué es esto; mandarlo a un
              formulario de acceso es cerrarle la puerta. Antes este lugar lo
              ocupaba un enlace al programa de una institución con pacientes
              reales: no corresponde ofrecerlo como muestra. */}
          <Link to="/simulacion">
            <Button size="lg" variant="white" className="group">
              Probar la plataforma
              <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
            </Button>
          </Link>
          <Link
            to="/ingresar"
            className="text-sm font-medium text-white/70 hover:text-white transition-colors duration-200 px-2"
          >
            Ya tengo cuenta
          </Link>
        </div>

      </div>
    </section>
  );
}

const CAPABILITIES = [
  {
    icon: SlidersHorizontal,
    title: 'Seguimiento configurable',
    body: 'Síntomas, peso, alimentación y adherencia. La institución elige qué se mide, con qué escala y cada cuánto.',
  },
  {
    icon: BellRing,
    title: 'Alertas con reglas propias',
    body: 'Pérdida de peso, dolor severo, fiebre o adherencia baja disparan un aviso al equipo de seguimiento.',
  },
  {
    icon: LayoutDashboard,
    title: 'Resultados medibles',
    body: 'Engagement, adherencia y evolución de síntomas por cohorte, para justificar el acompañamiento con datos.',
  },
  {
    icon: ShieldCheck,
    title: 'Listo para compliance',
    body: 'Consentimiento versionado, auditoría de accesos y derivación de eventos adversos a farmacovigilancia.',
  },
];

function Capabilities() {
  return (
    <section className="mx-auto max-w-7xl px-6 py-12 md:py-16">
      <Reveal>
        <Eyebrow>Qué incluye</Eyebrow>
        <h2 className="mt-3 max-w-2xl text-3xl md:text-4xl font-semibold tracking-[-0.02em] leading-tight">
          Todo lo que un programa de pacientes necesita para funcionar de verdad.
        </h2>
      </Reveal>

      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {CAPABILITIES.map((item, index) => (
          <Reveal key={item.title} delay={index * 80}>
            <article className="h-full rounded-lg border border-border bg-surface-raised p-6 shadow-sm tr-lift hover:border-primary/40 group">
              <div
                className="size-11 rounded-md grid place-items-center text-white transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3"
                style={{ background: 'var(--tr-gradient)' }}
              >
                <item.icon className="size-5" />
              </div>
              <h3 className="mt-5 font-semibold text-[17px] tracking-[-0.01em]">{item.title}</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-fg-muted">{item.body}</p>
            </article>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

const STEPS = [
  {
    icon: Palette,
    step: '01',
    title: 'La institución carga su marca',
    body: 'Logo, colores, tipografía y textos. La app del paciente pasa a ser suya.',
  },
  {
    icon: Stethoscope,
    step: '02',
    title: 'Elige qué medir',
    body: 'Del catálogo clínico o desde cero. ESAS-r, PRO-CTCAE, peso, alimentación, adherencia.',
  },
  {
    icon: BellRing,
    step: '03',
    title: 'Define qué lo alarma',
    body: 'Fiebre, caída de peso, adherencia baja. Cada regla llega a la cola del equipo de seguimiento.',
  },
];

function HowItWorks() {
  return (
    <section className="bg-surface-tinted/50 py-12 md:py-16">
      <div className="mx-auto max-w-7xl px-6">
        <Reveal>
          <Eyebrow>Cómo funciona</Eyebrow>
          <h2 className="mt-3 max-w-2xl text-3xl md:text-4xl font-semibold tracking-[-0.02em] leading-tight">
            Tres pasos, ninguno técnico.
          </h2>
        </Reveal>

        <div className="mt-8 grid md:grid-cols-3 gap-5">
          {STEPS.map((item, index) => (
            <Reveal key={item.step} delay={index * 100}>
              <article className="relative h-full rounded-lg border border-border bg-surface-raised p-7 shadow-sm tr-lift overflow-hidden group">
                <span
                  className="absolute -top-3 -right-1 text-[5rem] font-bold leading-none text-fg opacity-[0.04] transition-opacity duration-300 group-hover:opacity-[0.08] select-none"
                  aria-hidden
                >
                  {item.step}
                </span>
                <item.icon className="size-6 text-primary" />
                <h3 className="mt-4 font-semibold text-[17px] tracking-[-0.01em]">{item.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-fg-muted">{item.body}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Closing() {
  return (
    <section className="mx-auto max-w-7xl px-6 py-12 md:py-16">
      <Reveal>
        <div
          className="relative overflow-hidden rounded-xl px-8 py-14 md:px-14 md:py-16 text-white"
          style={{ background: '#1a1f2e' }}
        >
          <div
            className="pointer-events-none absolute -bottom-32 -right-20 size-[540px] rounded-full blur-3xl opacity-60 animate-drift"
            style={{ background: 'radial-gradient(circle, rgba(51,92,255,0.6), transparent 65%)' }}
            aria-hidden
          />
          <div
            className="pointer-events-none absolute -top-32 -left-16 size-[420px] rounded-full blur-3xl opacity-50 animate-drift"
            style={{
              background: 'radial-gradient(circle, rgba(229,119,251,0.5), transparent 65%)',
              animationDelay: '-7s',
            }}
            aria-hidden
          />

          <div className="relative">
            <h2 className="max-w-xl text-3xl md:text-4xl font-semibold tracking-[-0.02em] leading-tight">
              Un mismo software, la marca de cada institución.
            </h2>
            {/* Los enlaces a programas reales se fueron de la página pública.
                Prometían "entrá sin loguearte" y llevaban a una pantalla de
                ingreso, además de exponer el nombre de un cliente. En su lugar
                va la simulación, que es lo que prometían y no cumplían. */}
            <p className="mt-4 max-w-lg text-white/70 leading-relaxed">
              Cada institución define su nombre, su logo, sus colores y sus textos. El paciente
              ve el programa de su institución.
            </p>

            <Link to="/simulacion" className="mt-8 inline-block">
              <Button size="lg" variant="white" className="group">
                Recorrer una simulación
                <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
